
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PawPrint, MessageSquare, Share, Camera, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LikeButton from './LikeButton';
import PostCardActions from './PostCardActions';
import ShareModal from './ShareModal';
import FollowButton from './FollowButton';
import MediaCarousel from './MediaCarousel';
import { MatchButton } from './MatchButton';

interface PostCardProps {
  post: {
    id: string;
    caption: string;
    created_at: string;
    media: { url: string; media_type?: string }[];
    pet: {
      id: string;
      name: string;
      photo_url?: string;
      user_id?: string;
      species?: string;
      is_memorial?: boolean;
    };
    likes_count: number;
    comments_count: number;
    user_has_liked: boolean;
    user_id?: string;
    post_type?: string;
    privacy_setting?: 'public' | 'private' | 'followers_only';
  };
}

const PostCard = ({ post }: PostCardProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { t } = useTranslation();

  
  // ✅ NEW: Helper function to get post type indicator
  const getPostTypeIndicator = () => {
    if (post.post_type === 'profile_photo') {
      return (
        <div className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
          <Camera className="h-3 w-3 mr-1" />
          <span>{t('postTypes.profilePhoto.badge')}</span>
        </div>
      );
    }
    return null;
  };
  
  const isMemorial = post.pet.is_memorial === true;
  
  return (
    <div className={`mb-8 md:rounded-lg md:overflow-hidden md:shadow-md ${isMemorial ? 'memorial-celebration-border' : ''}`}>
      {/* Post Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <Link to={`/pet/${post.pet.id}`} className="flex items-center">
          <div className={`mr-2 ${isMemorial ? 'memorial-avatar-halo rounded-full' : ''}`}>
            <Avatar className="h-8 w-8">
              {post.pet.photo_url ? (
                <AvatarImage src={post.pet.photo_url} alt={post.pet.name} />
              ) : (
                <AvatarFallback className="bg-pet-purple/20 text-pet-purple">
                  <PawPrint className="h-4 w-4" />
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium hover:underline">{post.pet.name}</span>
              {isMemorial && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs italic text-white bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 whitespace-nowrap">
                  {t('common.foreverLoved')}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </span>
          </div>
        </Link>
        
        <div className="flex items-center gap-2">
          {/* Match button */}
          {(post.user_id || post.pet.user_id) && (
            <MatchButton 
              petId={post.pet.id} 
              petName={post.pet.name}
              petSpecies={post.pet.species}
              ownerId={post.user_id || post.pet.user_id}
              isMemorial={post.pet.is_memorial}
            />
          )}
          
          {/* Follow button for post author */}
          {(post.user_id || post.pet.user_id) && (
            <FollowButton targetUserId={post.user_id || post.pet.user_id!} />
          )}
        </div>
      </div>

      {/* Post Media - Carousel for multi-media posts */}
      {post.media.length > 0 && (
        <Link to={`/post/${post.id}`} className="block">
          <div className="relative w-full max-h-[75vh] overflow-hidden bg-gray-100 flex items-center justify-center">
            <MediaCarousel
              media={post.media}
              alt={post.caption || 'Post image'}
              containerClassName="max-h-[75vh]"
            />
          </div>
        </Link>
      )}

      {/* Post Type Indicator */}
      {getPostTypeIndicator() && (
        <div className="px-4 pt-3">
          {getPostTypeIndicator()}
        </div>
      )}

      {/* Post Caption */}
      {post.caption && (
        <div className="p-4">
          <p className="text-sm text-gray-700">{post.caption}</p>
        </div>
      )}

      {/* Post Actions */}
      <div className="px-4 py-2 flex justify-between">
        <div className="flex items-center space-x-3">
          <LikeButton 
            id={post.id}
            showModal={true}
          />
          <Link to={`/post/${post.id}`} className="flex items-center text-gray-500 hover:text-gray-700">
            <MessageSquare className="h-5 w-5 mr-1" />
            <span>{post.comments_count || 0}</span>
          </Link>
          <button 
            onClick={() => setIsShareModalOpen(true)} 
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <Share className="h-5 w-5 mr-1" />
            <span>Share</span>
          </button>
        </div>
        <PostCardActions 
          postId={post.id} 
          petName={post.pet?.name} 
          userId={post.user_id}
          caption={post.caption}
          privacySetting={post.privacy_setting}
        />
      </div>
      
      {/* Share Modal */}
      <ShareModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)} 
        postId={post.id}
        petName={post.pet.name}
      />
    </div>
  );
};

export default PostCard;
